import {
	FieldValues,
	FieldPath,
	useController,
	UseControllerProps,
	Control,
} from 'react-hook-form';
import {
	Checkbox,
	CheckboxProps,
	FormControl,
	FormLabel,
	FormGroup,
	FormControlLabel,
	FormHelperText,
} from '@mui/material';

interface CheckboxOption {
	value: string;
	label: string;
}

interface CheckboxGroupProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends Omit<UseControllerProps<TFieldValues, TName>, 'name'> {
	name: TName;
	label?: string;
	options: CheckboxOption[];
	helperText?: string;
	row?: boolean;
	checkboxProps?: Omit<CheckboxProps, 'checked' | 'onChange'>;
}

export default function AppCheckBoxGroup<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
	name,
	label,
	options,
	helperText,
	row = false,
	control,
	rules,
	defaultValue,
	shouldUnregister,
	checkboxProps,
	...props
}: CheckboxGroupProps<TFieldValues, TName> & CheckboxProps) {
	const {
		field,
		fieldState: { error },
	} = useController<TFieldValues, TName>({
		name,
		control,
		rules,
		defaultValue,
		shouldUnregister,
	});

	const errorMessage = error?.message;
	const selectedValues: any = Array.isArray(field.value) ? field.value : [];

	const handleChange =
		(optionValue: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
			const newValue: any = [...selectedValues];

			if (event.target.checked) {
				if (!newValue.includes(optionValue)) {
					newValue.push(optionValue);
				}
			} else {
				const index = newValue.indexOf(optionValue);
				if (index > -1) {
					newValue.splice(index, 1);
				}
			}

			field.onChange(newValue);
		};

	return (
		<FormControl error={!!error} variant='standard' sx={{ padding: '0 1rem' }}>
			{label && <FormLabel component='legend'>{label}</FormLabel>}
			<FormGroup row={row}>
				{options.map((option) => (
					<FormControlLabel
						key={option.value}
						control={
							<Checkbox
								{...checkboxProps}
								checked={selectedValues.includes(option.value)}
								onChange={handleChange(option.value)}
							/>
						}
						label={option.label}
					/>
				))}
			</FormGroup>
			{helperText && <FormHelperText>{helperText}</FormHelperText>}
			{errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
		</FormControl>
	);
}
